import { useCallback } from 'react';

import { GET_VEHICLE_BY_LICENSE_PLATE_NUMBER } from '../../../shared/graphql/license-plate-queries';
import { GET_MODELS } from '../graphql/fitment-queries';

import { useGraphQLClient, useModal } from '../../../shared/hooks/hooks';

import { trackAction } from '../../../shared/utils/analytics/analytics';

import { VehicleData } from '../../../shared/types/graphql-types';
import { LICENSE_PLATE_LOOKUP_MODAL } from '../../../shared/constants/modals';

function useLicensePlate() {
  const client = useGraphQLClient();
  const { openModal } = useModal();

  const updateFitmentWithLicensePlateDecodeData = useCallback(
    async (
      vehicleData,
      onYearSelected,
      onMakeSelected,
      onModelSelected,
      onTrimSelected,
      onAssemblySelected,
      onVehicleConfirmation
    ) => {
      try {
        const flattenedVehicleData = {
          ...vehicleData.vehicleAssembly?.vehicle,
          assemblyId: vehicleData.vehicleAssembly?.assembly?.id,
          frontAssembly: vehicleData.frontAssembly,
          isStaggered: vehicleData.isStaggered,
          rearAssembly: vehicleData.rearAssembly,
          rideHeights: vehicleData.rideHeights,
          trimId: vehicleData.trimId,
          trimName: vehicleData.trimName
        };

        onYearSelected({
          description: flattenedVehicleData.year,
          id: flattenedVehicleData.year
        });
        onMakeSelected(flattenedVehicleData?.make);
        const resp = await client.query({
          query: GET_MODELS,
          variables: {
            forMake: flattenedVehicleData.make,
            forYear: flattenedVehicleData.year
          }
        });
        const { data } = resp;
        const models = data?.vehicleSelection?.models;
        const selectedModel = models?.find(
          model => model?.description === flattenedVehicleData.model
        );
        onModelSelected(selectedModel);
        if (flattenedVehicleData?.trimId) {
          onTrimSelected({
            description: flattenedVehicleData?.trimName,
            id: flattenedVehicleData?.trimId
          });
        }
        if (
          flattenedVehicleData?.assemblyId &&
          flattenedVehicleData?.frontAssembly
        ) {
          onAssemblySelected({
            description: flattenedVehicleData?.frontAssembly,
            id: flattenedVehicleData?.assemblyId,
            isOE: !flattenedVehicleData.isNonOE
          });
        }
        onVehicleConfirmation?.({
          assemblyId: flattenedVehicleData?.assemblyId,
          frontAssembly: flattenedVehicleData?.frontAssembly,
          make: flattenedVehicleData?.make,
          model: selectedModel?.description,
          trimDesc: flattenedVehicleData?.trimName,
          trimId: flattenedVehicleData?.trimId,
          vehicleId: selectedModel?.id,
          year: flattenedVehicleData?.year
        });
      } catch (err) {
        console.error('Error fetching models:', err);
      }
    },
    [client]
  );

  const getVehicleByLicensePlate = useCallback(
    async (
      variables,
      onYearSelected,
      onMakeSelected,
      onModelSelected,
      onTrimSelected,
      onAssemblySelected,
      onVehicleConfirmation,
      onCancel,
      getFitment,
      onFitment,
      isModalEmbedded
    ) => {
      let vehicleData: VehicleData = {} as VehicleData;
      client
        .query({
          fetchPolicy: 'network-only',
          query: GET_VEHICLE_BY_LICENSE_PLATE_NUMBER,
          variables
        })
        .then(data => {
          vehicleData =
            data.data.vehicleSelection.vehicleAssemblyByLicensePlateNo;
          openModal(LICENSE_PLATE_LOOKUP_MODAL, {
            getFitment,
            isModalEmbedded: isModalEmbedded,
            licensePlate: variables.licensePlate,
            onClose: onCancel,
            onFitment: onFitment,
            onVehicleSelect: async () => {
              await updateFitmentWithLicensePlateDecodeData(
                vehicleData,
                onYearSelected,
                onMakeSelected,
                onModelSelected,
                onTrimSelected,
                onAssemblySelected,
                onVehicleConfirmation
              );
            },
            vehicleData
          });
        });
    },
    [client, openModal, updateFitmentWithLicensePlateDecodeData]
  );

  const onSubmitLicensePlate = useCallback(
    async (
      licensePlateQueryVariables,
      onYearSelected,
      onMakeSelected,
      onModelSelected,
      onTrimSelected,
      onAssemblySelected,
      onVehicleConfirmation,
      onCancel,
      getFitment,
      onFitment,
      isModalEmbedded
    ) => {
      trackAction('vehicle_license_submit');
      getVehicleByLicensePlate(
        licensePlateQueryVariables,
        onYearSelected,
        onMakeSelected,
        onModelSelected,
        onTrimSelected,
        onAssemblySelected,
        onVehicleConfirmation,
        onCancel,
        getFitment,
        onFitment,
        isModalEmbedded
      );
    },
    [getVehicleByLicensePlate]
  );

  return {
    onSubmitLicensePlate
  };
}

export default useLicensePlate;
